import { Empty, Table, Alert, Typography } from 'antd';
import classes from './DocumentsResearch.module.scss';
import { getDocumentInfoColumns } from './helpers/columns';
import { Trans, useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import prepareDocumentInfo from './helpers/prepareDocumentInfo';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import LogoType from 'assets/img';
import { useNavigate } from 'react-router';
import ReportFooter from './ReportFooter';
import { fetchRoom } from 'store/reducers/roomsCreator';
import { setUserAndRoomStatus } from 'store/slices/userDataSlice';
import { setUserPermissions } from 'store/slices/dataPermissionsSlice';
import { fetchUserPermissions } from 'store/reducers/userDataCreator';

const { Title } = Typography;

const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME || 'tazadeal';

const getIcon = (extension: string) => {
  const typeVersion = availableIconFormat.includes(extension)
    ? extension
    : 'default';
  const iconVersion = typeVersion as keyof typeof documnentIcons;
  return iconVersion as keyof typeof documnentIcons;
};

export default function DocumentInfo() {
  const { t } = useTranslation();
  const appName = buildName === 'athena' ? 'Athena' : 'TazaDeal';
  const navigate = useNavigate();

  const rawDocumentData = useAppSelector(
    (state) => state.research.reportDocumentRawData
  );
  const userData = useAppSelector((state) => state.userData.userData);
  const dispatch = useAppDispatch();

  const processedData = useMemo(() => {
    if (!rawDocumentData || !userData) return null;
    return prepareDocumentInfo(rawDocumentData, t, userData);
  }, [rawDocumentData, userData, t]);

  const handleRoomNavigation = React.useCallback(async () => {
    if (rawDocumentData?.previous?.room_id) {
      const roomId = rawDocumentData.previous.room_id;
      navigate(`/room/${roomId}/documents`);
      dispatch(setUserPermissions(null));
      await dispatch(fetchUserPermissions({ idRoom: roomId }));
      await dispatch(fetchRoom({ id: roomId }));
      dispatch(setUserAndRoomStatus('fulfilledDataRoom'));    
    }
  }, [rawDocumentData, navigate, dispatch]);

  const infoColumns = getDocumentInfoColumns(handleRoomNavigation, t);

  const signedUsers = useMemo(() => {
    if (!rawDocumentData?.signatures || rawDocumentData.signatures.length === 0) return [];

    return rawDocumentData.signatures.map(signature => ({
      name: signature.author,
      email: signature.email,
      date: signature.timestamp,
    }));
  }, [rawDocumentData?.signatures]);

  const signatureStatus = useMemo(() => {
    if (!rawDocumentData?.signatures || rawDocumentData.signatures.length === 0) return 'absent';
    return 'signed';
  }, [rawDocumentData?.signatures]);

  const updatedSignatureInfo = useMemo(() => {
    return [
      {
        key: t('Report.info.status'),
        value: t(`Report.info.${signatureStatus}`),
      },
    ];
  }, [signatureStatus, t]);

  if (!processedData?.documentInfoData) {
    return (
      <div className={classes.info}>
        <Alert
          message={<b>{t('Report.info.noDigitalMark')}</b>}
          description={
            <div>
              <Trans
                i18nKey='Report.info.noDigitalMarkMessage'
                t={t}
                values={{ appName }}
                components={{ b: <b /> }}
              />
            </div>
          }
          type='warning'
          style={{ margin: '20px 0' }}
        />
      </div>
    );
  }

  return (
    <div className={classes.info}>
      <div className={classes.info_header}>
        <div className={classes.info_header_title}>
          <Title level={4}>
            <span>{t('Report.report.title')}</span>
            <div>
              <Title level={5}>
                {
                  documnentIcons[
                    getIcon(rawDocumentData?.current?.extension || '')
                  ]
                }{' '}
                {rawDocumentData?.current?.name}
              </Title>
            </div>
          </Title>
        </div>
        <img src={LogoType} alt='Logotype' className={classes.logo} />
      </div>

      <div className={classes.document_notices}>
        <div className={classes.document_copy_notice}>
          {t('Report.info.documentCopyNotice', {appName})}
        </div>
        <div className={classes.document_details_title}>
          {t('Report.info.documentDetails')}
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <Title level={5} className={classes.title}>
          {t('Report.info.uploadedOriginal')}
        </Title>
        <Table
          dataSource={processedData?.documentInfoData?.uploadInfo}
          columns={infoColumns}
          className={classes.infoTable}
          pagination={false}
          size='small'
          showHeader={false}
          style={{ marginBottom: '16px' }}
          locale={{
            emptyText: (
              <div className={classes.emptyText}>
                <Empty description={t('Report.info.noData')} />
              </div>
            ),
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Title level={5} className={classes.title}>
          {t('Report.info.copyDownloaded')}
        </Title>
        <Table
          dataSource={processedData?.documentInfoData?.downloadInfo}
          columns={infoColumns}
          className={classes.infoTable}
          pagination={false}
          size='small'
          showHeader={false}
          style={{ marginBottom: '16px' }}
          locale={{
            emptyText: (
              <div className={classes.emptyText}>
                <Empty description={t('Report.info.noData')} />
              </div>
            ),
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Title level={5} className={classes.title}>
          {t('Report.info.digitalSignature')}
        </Title>
        <Table
          dataSource={updatedSignatureInfo}
          columns={infoColumns}
          className={classes.infoTable}
          pagination={false}
          size='small'
          showHeader={false}
          locale={{
            emptyText: (
              <div className={classes.emptyText}>
                <Empty description={t('Report.info.noData')} />
              </div>
            ),
          }}
        />
      </div>
       {signatureStatus === 'signed' && signedUsers.length > 0 && ( 
         <div style={{ marginBottom: '16px' }}>
           <Title level={5} className={classes.title}>
             {t('Report.info.signedByUsers')}
           </Title>
           <Table
             dataSource={signedUsers.map((user, index) => ({
               key: index,
               userInfo: `${user.name} ${user.email}`,
               deviceInfo: `IP ${rawDocumentData?.signatures?.[index]?.ip || '-'} ${t('Report.info.browser_version')} ${rawDocumentData?.signatures?.[index]?.browser_version || '-'}`,
             }))}
             columns={[
               {
                 title: '',
                 dataIndex: 'userInfo',
                 key: 'userInfo',
                 width: '40%',
               },
               {
                 title: '',
                 dataIndex: 'deviceInfo',
                 key: 'deviceInfo',
                 width: '60%',
               },
             ]}
             className={classes.infoTable}
             pagination={false}
             size='small'
             showHeader={false}
             locale={{
               emptyText: (
                 <div className={classes.emptyText}>
                   <Empty description={t('Report.info.noData')} />
                 </div>
               ),
             }}
           />
         </div>
       )}

      <ReportFooter />
    </div>
  );
}
